import React from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function WhyThisCourse() {
  const { ref: sectionRef, isVisible: sectionVisible } = useScrollAnimation();
  const { ref: timelineRef, isVisible: timelineVisible } = useScrollAnimation({
    threshold: 0.1,
  });

  return (
    <section
      id="why-this-course"
      ref={sectionRef as React.RefObject<HTMLElement>}
      className="py-24 lg:py-32 bg-white"
    >
      <div className="max-w-5xl mx-auto px-6 lg:px-12 text-center">
        <div
          className={`transition-all duration-700 ease-out ${
            sectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="mb-6">
            <span className="text-xs uppercase tracking-widest text-[#BA3D03] font-semibold">
              CONTEXT & RELEVANCE
            </span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0E0503] mb-8 leading-tight">
            Country-led practice is not optional anymore. It is how projects succeed.
          </h2>

          <div className="space-y-6 text-lg text-[#3D1203] leading-relaxed max-w-3xl mx-auto">
            <p>
              The Better Placed Framework establishes seven objectives for delivering high-quality
              built environments in NSW. Connecting with Country helps project teams interpret these
              objectives through a cultural lens and apply them in planning, design and long term
              stewardship.
            </p>
            <p>
              Cultural understanding does not happen by default. Teams need genuine knowledge,
              practical tools and the confidence to work differently — connecting with Country
              meaningfully across the full project lifecycle, not just at key approval gates.
            </p>
            <p>
              This course builds the cultural knowledge and practical capability your team needs.
              It equips your team to understand Country as a living cultural system, recognise
              Traditional Custodians as knowledge holders and rights holders, protect Indigenous
              Cultural and Intellectual Property, create physically and culturally safe project
              environments, and structure meaningful engagement with Traditional Custodians and
              Indigenous organisations. You will also learn a four phase implementation model:
              Starting with Country, Imagining with Country, Shaping Country, Caring for Country.
            </p>
          </div>

          {/* Callout block */}
          <div className="mt-12 mx-auto max-w-3xl">
            {/* Accent line */}
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent to-[#E48523]"></div>
              <div className="w-2 h-2 rounded-full bg-[#BA3D03]"></div>
              <div className="h-px flex-1 bg-gradient-to-l from-transparent to-[#E48523]"></div>
            </div>

            <div className="relative bg-[#FFF9F5] border-l-4 border-[#BA3D03] rounded-r-xl px-8 py-6 text-left shadow-sm">
              <i className="ri-double-quotes-l absolute top-4 left-4 text-[#E48523] text-3xl opacity-30"></i>
              <p className="font-bold text-xl text-[#0E0503] leading-snug pl-4">
                This is not a decorative overlay of Indigenous elements. It is a structural shift in how projects are conceived, governed and delivered.
              </p>
            </div>
          </div>
        </div>

        {/* Timeline Graphic */}
        <div
          ref={timelineRef as React.RefObject<HTMLElement>}
          className={`
            mt-16 w-full max-w-4xl mx-auto
            transition-all duration-700 ease-out delay-200
            ${timelineVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}
          `}
        >
          <h3 className="text-xl sm:text-2xl font-bold text-[#0E0503] mb-8">
            From context to Country-centred delivery
          </h3>

          {/* Horizontally scrollable on mobile */}
          <div className="overflow-x-auto -mx-4 px-4 pb-2">
            <div className="relative w-full min-w-[480px]">
              {/* Connector lines between circles */}
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="absolute bg-[#E48523] z-0"
                  style={{
                    top: '1.75rem',
                    left: `${12.5 + i * 25 + 6}%`,
                    width: '13%',
                    height: '3px',
                  }}
                />
              ))}

              <div className="grid grid-cols-4 w-full">
                {[
                  { num: 1, label: 'Policy context', sub: 'Better Placed, 7 Objectives', color: 'bg-[#E48523]' },
                  { num: 2, label: 'What it requires', sub: 'Country, Custodians, ICIP, Cultural Safety', color: 'bg-[#BA3D03]' },
                  { num: 3, label: 'This Course', sub: 'Build genuine cultural capability', color: 'bg-[#3D1203]' },
                  { num: 4, label: 'Project Delivery', sub: 'Country-Centred Delivery', color: 'bg-[#E48523]' },
                ].map((item, index) => (
                  <div key={index} className="flex flex-col items-center text-center relative z-10 px-2">
                    <div
                      className={`w-14 h-14 rounded-full ${item.color} flex items-center justify-center text-white font-bold text-xl mb-3 shadow-md`}
                      style={{ transitionDelay: `${index * 100 + 300}ms` }}
                    >
                      {item.num}
                    </div>
                    <p className="text-sm font-semibold text-[#0E0503] leading-snug">{item.label}</p>
                    <p className="text-xs text-[#3D1203] opacity-70 leading-snug mt-0.5">{item.sub}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
